import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { Link, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MrtColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import {
    ActivitiesFormData,
    ActivitiesFormIngredientSupplier,
} from "src/pages/Ingredient/IngredientDetails/ActivitiesData/Activities.type";

import { IngredientSupplierActivityData } from "src/api/client-api/ingredients/useGetOneIngredient";
import { isSupplierManagedBy } from "./isSupplierManagedBy";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Campaign } from "src/api/client-api/useCampaign";
import { SalesBox } from "src/components/ActivityDataGrid/Box/SalesBox";
import { InputBox } from "src/components/ActivityDataGrid/Box/InputBox";

type ActivitiesFormIngredientSupplierKeys = keyof Pick<IngredientSupplierActivityData, "quantity" | "grossSales">;

export const useActivitiesColumns = (
    toggleEditMode: boolean,
    activitiesData: ActivitiesFormData,
    setActivitiesData: (value: ActivitiesFormData) => void,
): MrtColumns<ActivitiesFormIngredientSupplier> => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentCampaign, selectedCompany } = useFoodpilotContext();

    const columnHelper = createMRTColumnHelper<ActivitiesFormIngredientSupplier>();

    const mrt_columns = [
        columnHelper.accessor("supplier.name", {
            header: t("ingredient.activityform.supplier"),
            Cell: (value) => (
                <Link sx={{ color: `${theme.palette.primary[700]}`, textDecoration: "none" }} variant="h5">
                    {value.renderedCellValue}
                </Link>
            ),
        }),
        columnHelper.accessor("ingredientSupplierActivityDatas.quantity", {
            header: t("ingredient.activityform.quantity"),
            Cell: (value) => {
                const activityDatas = value.row.original.ingredientSupplierActivityDatas ?? [];

                const quantity = activityDatas.reduce((acc, activityData) => {
                    return acc + (activityData.quantity || 0);
                }, 0);

                return (
                        toggleEditMode &&
                            activityDatas.length <= 1 &&
                            isSupplierManagedBy(value.row.original, selectedCompany?.id)
                    ) ?
                        <InputBox
                            value={quantity.toString()}
                            unit={"T."}
                            updateActivities={(newValue) =>
                                updateActivitiesData(
                                    value.row.original.id,
                                    newValue,
                                    "quantity",
                                    activitiesData,
                                    setActivitiesData,
                                    currentCampaign,
                                )
                            }
                        />
                    :   <SalesBox value={quantity} unit={"T."} />;
            },
        }),
        columnHelper.accessor("ingredientSupplierActivityDatas.grossSales", {
            header: t("ingredient.activityform.grosssales"),
            Cell: (value) => {
                const activityDatas = value.row.original.ingredientSupplierActivityDatas ?? [];

                const grossSales = activityDatas.reduce((acc, activityData) => {
                    return acc + (activityData.grossSales || 0);
                }, 0);

                return (
                        toggleEditMode &&
                            activityDatas.length <= 1 &&
                            isSupplierManagedBy(value.row.original, selectedCompany?.id)
                    ) ?
                        <InputBox
                            value={grossSales.toString()}
                            unit="€"
                            updateActivities={(newValue) =>
                                updateActivitiesData(
                                    value.row.original.id,
                                    newValue,
                                    "grossSales",
                                    activitiesData,
                                    setActivitiesData,
                                    currentCampaign,
                                )
                            }
                        />
                    :   <SalesBox value={grossSales} unit="€" />;
            },
        }),
        columnHelper.accessor("ingredientSupplierActivityDatas.averagePrice", {
            header: t("ingredient.activityform.averageprice"),
            Cell: (value) => {
                const activityDatas = value.row.original.ingredientSupplierActivityDatas ?? [];

                const quantity = activityDatas.reduce((acc, activityData) => {
                    return acc + (activityData.quantity || 0);
                }, 0);

                const grossSales = activityDatas.reduce((acc, activityData) => {
                    return acc + (activityData.grossSales || 0);
                }, 0);

                const averagePrice = quantity !== 0 ? grossSales / quantity : undefined;

                return <SalesBox value={averagePrice ? averagePrice.toString() : undefined} unit={"€/T."} />;
            },
        }),
    ] as MRT_ColumnDef<ActivitiesFormIngredientSupplier, string>[];

    return mrt_columns;
};

const updateActivitiesData = (
    supplierId: number | undefined,
    value: string,
    key: ActivitiesFormIngredientSupplierKeys,
    activitiesData: ActivitiesFormData,
    setActivitiesData: (value: ActivitiesFormData) => void,
    campaign: Campaign | null,
) => {
    if (!supplierId || !campaign) return;

    const supplierIndex = activitiesData.suppliers.findIndex((supplier) => supplier.id === supplierId);

    if (supplierIndex === -1) return;

    const newSupplier = activitiesData.suppliers[supplierIndex];

    if (newSupplier.ingredientSupplierActivityDatas?.length === 0) {
        newSupplier.ingredientSupplierActivityDatas.push({
            grossSales: 0,
            quantity: 0,
            averagePrice: 0,
            campaign: `/api/campaigns/${campaign.id}`,
        });
    }

    // We can see only our own data so we take the 1st record
    const ourActivityData = newSupplier.ingredientSupplierActivityDatas?.[0];

    if (ourActivityData === undefined) return;

    ourActivityData[key] = parseInt(value);

    const grossSales = ourActivityData["grossSales"] ?? 0;
    const quantity = ourActivityData["quantity"] ?? 0;

    ourActivityData["averagePrice"] = quantity ? grossSales / quantity : undefined;

    const updatedSupplierData = activitiesData.suppliers.toSpliced(supplierIndex, 1, newSupplier);

    setActivitiesData({ suppliers: updatedSupplierData });
};
